import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { HiOutlineCube } from 'react-icons/hi';

import { useTranslation } from '@/i18n';

type NotFoundPageProps = {
  variant?: 'tracking' | 'page';
  fullPage?: boolean;
};

const NotFoundPage = ({
  variant = 'tracking',
  fullPage = false,
}: NotFoundPageProps) => {
  const { t } = useTranslation();

  const title =
    variant === 'tracking'
      ? t('notFound.trackingTitle')
      : t('notFound.pageTitle');

  const description =
    variant === 'tracking'
      ? t('notFound.trackingDescription')
      : t('notFound.pageDescription');

  return (
    <div
      className={`relative flex w-full flex-1 flex-col items-center justify-center overflow-hidden px-4 py-12 sm:px-6 sm:py-16 ${
        fullPage ? '' : 'min-h-[calc(100dvh-12rem)]'
      }`}
    >
      <div
        aria-hidden
        className='pointer-events-none absolute inset-0 bg-[#eef3f4]'
      />
      <div
        aria-hidden
        className='pointer-events-none absolute inset-0 opacity-[0.35]'
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(9,45,54,0.08) 1px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />
      <div
        aria-hidden
        className='pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-[#449486]/15 blur-3xl'
      />
      <div
        aria-hidden
        className='pointer-events-none absolute -right-16 bottom-0 h-96 w-96 rounded-full bg-[#092d36]/10 blur-3xl'
      />

      <div className='relative z-10 flex w-full max-w-2xl flex-col items-center text-center'>
        <div className='relative mb-10 flex h-52 w-full max-w-sm items-center justify-center sm:mb-12 sm:h-56'>
          <span
            aria-hidden
            className='select-none font-headings-semibold text-[8.5rem] leading-none tracking-tight text-[#092d36]/[0.05] sm:text-[10rem]'
          >
            {t('notFound.code')}
          </span>

          <div className='absolute flex items-center justify-center'>
            <div className='absolute h-44 w-44 rounded-full border border-greenSecondary/15 sm:h-48 sm:w-48' />
            <div className='absolute h-32 w-32 rounded-full border border-dashed border-greenSecondary/25 sm:h-36 sm:w-36' />
            <div className='absolute h-24 w-24 rounded-full bg-greenSecondary/10 blur-xl' />

            <div className='relative z-10 flex flex-col items-center gap-5'>
              <div className='tracking-pill flex h-24 w-24 items-center justify-center rounded-full shadow-pill sm:h-28 sm:w-28'>
                <HiOutlineCube
                  aria-hidden
                  className='h-11 w-11 text-white sm:h-12 sm:w-12'
                />
              </div>
              <p className='bg-gradient-to-r from-backgroundGreen via-[#0c3a44] to-greenSecondary bg-clip-text font-headings-semibold text-5xl text-transparent sm:text-6xl'>
                {t('notFound.code')}
              </p>
            </div>
          </div>
        </div>

        <div className='flex w-full max-w-xl flex-col items-center px-2'>
          <div
            aria-hidden
            className='mb-8 flex w-full max-w-[220px] items-center gap-3 sm:mb-9'
          >
            <span className='h-px flex-1 bg-gradient-to-r from-transparent to-greenSecondary/45' />
            <span className='h-1 w-1 rounded-full bg-greenSecondary/70' />
            <span className='h-px flex-1 bg-gradient-to-l from-transparent to-greenSecondary/45' />
          </div>

          <div className='flex flex-col items-center gap-5 sm:gap-6'>
            <h1 className='relative max-w-md font-headings-semibold'>
              <span className='block bg-gradient-to-br from-backgroundGreen via-[#0a3540] to-greenSecondary bg-clip-text text-[2rem] leading-[1.12] tracking-[-0.035em] text-transparent sm:text-[2.75rem] sm:leading-[1.1]'>
                {title}
              </span>
              <span
                aria-hidden
                className='mx-auto mt-4 block h-[3px] w-14 rounded-full bg-gradient-to-r from-greenSecondary/25 via-greenSecondary to-greenSecondary/25 sm:mt-5 sm:w-16'
              />
            </h1>

            <p className='max-w-[22rem] font-content text-[1.0625rem] leading-[1.85] text-graySecondary sm:max-w-md sm:text-lg sm:leading-[1.9]'>
              <span
                aria-hidden
                className='mr-1.5 inline-block font-headings-semibold text-greenSecondary/35'
              >
                —
              </span>
              {description}
            </p>
          </div>
        </div>

        <Link className='group mt-12 w-full max-w-sm' to='/'>
          <span className='tracking-pill inline-flex w-full items-center justify-center gap-3 rounded-full px-8 py-4 text-sm font-semibold text-white shadow-pill transition-all duration-200 group-hover:-translate-y-0.5 group-hover:shadow-[0_16px_40px_rgba(9,45,54,0.28)] sm:text-base'>
            {t('notFound.backHome')}
            <FiArrowRight className='h-5 w-5 transition-transform duration-200 group-hover:translate-x-1' />
          </span>
        </Link>
      </div>
    </div>
  );
};

export { NotFoundPage };
